import { z } from "zod";
import { KomponentenZeileSchema, ScoreSchema } from "./common.js";

/**
 * Phase 4 — Kundenartikel-Match (Sonnet) gegen Tenant-Stammdaten.
 *
 * `matchScore` ist die EINZIGE Achse hier. Langtext- und Freigabe-Score
 * kommen erst in H7 dazu und werden in einem separaten Schema ergänzt
 * (NICHT zu einer Confidence gemerged).
 *
 * Status-Schwellen aus bidi_LV-Pattern:
 *  - matchScore >= 80 → "auto_uebernehmen"
 *  - matchScore >= 60 → "vorschlagen"
 *  - matchScore >= 40 → "pruefen"
 *  - matchScore <  40 → "nicht_geeignet"
 * Schwellen sind im Agent definiert, nicht hier — wir transportieren nur
 * den Status-String pro Treffer.
 */

export const MatchStatusSchema = z.enum([
  "auto_uebernehmen",
  "vorschlagen",
  "pruefen",
  "nicht_geeignet",
]);
export type MatchStatus = z.infer<typeof MatchStatusSchema>;

export const ArtikelTrefferSchema = z.object({
  artikelCode: z.string(),
  matchScore: ScoreSchema,
  status: MatchStatusSchema,
  begruendung: z.string(),
});
export type ArtikelTreffer = z.infer<typeof ArtikelTrefferSchema>;

/**
 * Schnellkalkulation als Alternative zum 1-Artikel-Pick: der Innendienst
 * baut aus mehreren Komponenten (Stammartikel + Hilfsartikel + freie Zeilen)
 * den Einheitspreis selbst zusammen. Jede Zeile traegt eine Kostenart,
 * woraus der Bepreisungs-Agent die EFB-223-Aufgliederung erzeugt.
 *
 * `mengenbezug`:
 *  - `pro_einheit` (Default): Komponenten beziehen sich auf 1 Einheit der
 *    LV-Position. Der Bepreisungs-Agent rechnet Σ(Komponenten) als
 *    Einzelpreis und multipliziert mit `pos.menge`.
 *  - `pauschal`: Σ(Komponenten) ist der Gesamtpreis der Position. Sinnvoll
 *    wenn die LV-Einheit `psch` ist oder die Komponenten bereits den
 *    vollen Auftrag abdecken.
 *
 * Mutually exclusive mit `gewaehlterArtikel`/`nichtAngeboten` (siehe
 * Backend-PATCH-Logik).
 */
export const SchnellkalkulationSchema = z.object({
  zeilen: z.array(KomponentenZeileSchema).min(1),
  mengenbezug: z.enum(["pro_einheit", "pauschal"]).default("pro_einheit"),
  /**
   * Preis-Bildung (aus der Artikel-Stückliste übernommen, sonst Default):
   *  - `summiert` (Default): Position-Preis = Σ der Komponenten-Zeilen
   *    (heutiges Verhalten).
   *  - `komplettpreis`: fester Einzelpreis (`komplettpreis`), die Komponenten
   *    dienen nur der EFB-223-Aufgliederung (proportional skaliert, sodass
   *    Σ Anteile == EP).
   */
  preisModus: z.enum(["summiert", "komplettpreis"]).default("summiert"),
  /** Fester Einzelpreis (VK pro 1 Einheit der Position) bei
   *  `preisModus="komplettpreis"`. */
  komplettpreis: z.number().nonnegative().optional(),
  notiz: z.string().optional(),
  /** Workflow-Flag: `false` = in Bearbeitung (Score 75 in der Übersicht),
   *  `true` = vom Innendienst final abgesegnet (Score 100). Default `false`
   *  bedeutet: alte Dateien ohne dieses Feld lesen sich automatisch als
   *  „noch nicht abgeschlossen". */
  closed: z.boolean().default(false),
});
export type Schnellkalkulation = z.infer<typeof SchnellkalkulationSchema>;

export const ArtikelMatchPositionSchema = z.object({
  positionId: z.string(),
  /** OZ aus der Konsolidierung — Redundanz, aber praktisch im Frontend. */
  oz: z.string(),
  /** Wie viele Pre-Filter-Kandidaten gab es? Hilft beim Debuggen wenn
   *  das LLM keine Treffer findet. */
  kandidatenAnzahl: z.number().int().nonnegative(),
  matchKandidaten: z.array(ArtikelTrefferSchema).default([]),
  /** User-Auswahl im Frontend. Initial `null` — der Match-Agent wählt nicht
   *  automatisch, der Innendienst entscheidet. Ausnahme: Pfad-B-Bemusterung
   *  (`vorausgewaehlterArtikelCode`) belegt das vor (explizite Innendienst-
   *  Entscheidung im Mengen-Tab), bleibt aber überschreibbar. */
  gewaehlterArtikel: z.string().nullable().default(null),
  /** Pfad B: Artikel, den der Innendienst in der Bemusterung gewählt hat
   *  (Transparenz fürs Frontend, z.B. Badge „aus Bemusterung"). Treibt die
   *  Vorbelegung von `gewaehlterArtikel`; NICHT in matchScore eingerechnet. */
  vorausgewaehlterArtikelCode: z.string().optional(),
  /** Wenn true: kein Kandidat überschritt die Vorschlags-Schwelle.
   *  Triggert in H4 einen Klärpunkt. */
  externeSucheNoetig: z.boolean().default(false),
  /** Bewusst aus dem Angebot ausgeschlossen — entweder manuell vom Innendienst
   *  oder beim Import via Tenant-Config (z.B. "wir bieten kein GaLa an").
   *  Bepreisungs-Agent + Angebots-Export überspringen diese Position. */
  nichtAngeboten: z.boolean().default(false),
  /** Wenn der LV-Originaltext Dienstleistungs-/Service-Verben enthält
   *  (z.B. „liefern, aufbringen"), markiert der Match-Agent das hier und
   *  cappt alle matchScore-Werte bei TenantConfig.matchPolicy.serviceCapScore.
   *  Frontend zeigt den Hinweis im Detail-Modal + ein Auto-Klärpunkt wird
   *  erstellt. `null` wenn kein Service erkannt wurde. */
  serviceErkannt: z
    .object({
      verben: z.array(z.string()).default([]),
      phrasen: z.array(z.string()).default([]),
      capApplied: z.number().int().min(0).max(100),
      /** Höchster Roh-LLM-Score VOR der Cap-Anwendung. Wird für die
       *  Telemetrie / Recommender genutzt — daraus leiten wir ab ob der
       *  Cap zu streng war (User pickt trotzdem) oder gerade richtig.
       *  `.optional()` damit Pipeline-Runs von vor Tranche B (ohne dieses
       *  Feld) sauber gelesen werden — Frontend fällt zurück auf capApplied. */
      topScoreBeforeCap: z.number().int().min(0).max(100).optional(),
    })
    .nullable()
    .default(null),
  /** Schnellkalkulation als dritte Aktion neben Pick/Exclude. Wenn != null,
   *  wird `gewaehlterArtikel` ignoriert (mutually exclusive — Backend
   *  setzt das beim Schreiben durch). Default null → kein Bruch fuer
   *  bestehende Pipeline-Runs. */
  schnellkalkulation: SchnellkalkulationSchema.nullable().default(null),
});
export type ArtikelMatchPosition = z.infer<typeof ArtikelMatchPositionSchema>;

export const ArtikelMatchSchema = z.object({
  /** v2 (2026-05): `schnellkalkulation` pro Position. v1-Dateien lesen
   *  weiterhin dank `.default(null)` — wir akzeptieren beide Versionen
   *  beim Lesen via `.union`. */
  schemaVersion: z.union([z.literal(1), z.literal(2)]).default(2),
  positionen: z.array(ArtikelMatchPositionSchema).default([]),
  /** Aggregat: Anzahl Positionen mit ≥1 Vorschlag. Für QS-Phase 98. */
  positionenMitMatch: z.number().int().nonnegative().default(0),
});

export type ArtikelMatch = z.infer<typeof ArtikelMatchSchema>;
